import { TabList as ChakraTabList, TabListProps as ChakraTabListProps, Box } from '@chakra-ui/react';
import { CSSObject } from '@emotion/react';
import PropTypes from 'prop-types';
import React, { forwardRef, useState, useRef, useEffect, useCallback } from 'react';

import TabListButtonLeft from './_TabListButtonLeft';
import TabListButtonRight from './_TabListButtonRight';
import DisplayNames from './enums';

export type TabListProps = ChakraTabListProps & {
  showScrollButtons?: boolean;
};

const propTypes = {
  showScrollButtons: PropTypes.bool
};

const defaultProps = {
  showScrollButtons: false
};

function checkTouchDevice() {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

function checkOverflow(element: HTMLElement) {
  return element.clientWidth < element.scrollWidth;
}

function checkScrollLeft(element: HTMLElement) {
  return element.scrollLeft === 0;
}

function checkScrollRight(element: HTMLElement) {
  return element.clientWidth + element.scrollLeft >= element.scrollWidth;
}

const scrollElementToLeft = (element: HTMLElement) => {
  element.scrollBy({ left: -element.clientWidth, behavior: 'smooth' });
};

const scrollElementToRight = (element: HTMLElement) => {
  element.scrollBy({ left: element.clientWidth, behavior: 'smooth' });
};

const TabList = forwardRef(
  (props: TabListProps, ref: React.MutableRefObject<HTMLDivElement>): React.ReactElement => {
    const newRef = useRef<HTMLElement>(null);
    const componentRef = ref || newRef;
    const { showScrollButtons, ...restProps } = props;

    const [isOverflowing, setIsOverflowing] = useState(false);
    const [isScrollLeftEdge, setIsScrollLeftEdge] = useState(false);
    const [isScrollRightEdge, setIsScrollRightEdge] = useState(false);
    const onClickButtonLeft = useCallback(() => {
      scrollElementToLeft(componentRef.current);
    }, [componentRef]);
    const onClickButtonRight = useCallback(() => {
      scrollElementToRight(componentRef.current);
    }, [componentRef]);

    const isTouchDevice = checkTouchDevice();
    const showLeftScrollButton = (!isTouchDevice || showScrollButtons) && isOverflowing && !isScrollLeftEdge;
    const showRightScrollButton = (!isTouchDevice || showScrollButtons) && isOverflowing && !isScrollRightEdge;

    useEffect(() => {
      const element = componentRef.current;
      const scrollListener = () => {
        setIsScrollLeftEdge(checkScrollLeft(element));
        setIsScrollRightEdge(checkScrollRight(element));
      };

      const overflowCheckResult = checkOverflow(element);
      setIsOverflowing(overflowCheckResult);

      setIsScrollLeftEdge(checkScrollLeft(element));
      setIsScrollRightEdge(checkScrollRight(element));
      element.addEventListener('scroll', scrollListener);

      return () => {
        element.removeEventListener('scroll', scrollListener);
      };
    }, [isScrollLeftEdge, isScrollRightEdge, componentRef]);

    const overflowStyle = isOverflowing ? 'hidden' : 'unset';
    const tabListBoxOverflowStyle = isTouchDevice ? 'auto' : overflowStyle;
    let tabListOverflowBoxStyles: CSSObject = {
      overflow: tabListBoxOverflowStyle
    };

    if (isOverflowing && !isTouchDevice) {
      tabListOverflowBoxStyles = {
        ...tabListOverflowBoxStyles,
        paddingTop: '2px',
        marginTop: '-2px'
      };
    }

    if (isOverflowing && !showLeftScrollButton) {
      tabListOverflowBoxStyles = {
        ...tabListOverflowBoxStyles,
        paddingLeft: '4px',
        marginLeft: '-4px'
      };
    }

    if (isOverflowing && !showRightScrollButton) {
      tabListOverflowBoxStyles = {
        ...tabListOverflowBoxStyles,
        paddingRight: '4px',
        marginRight: '-4px'
      };
    }

    return (
      <Box position={isOverflowing ? 'relative' : 'unset'}>
        <Box ref={componentRef} sx={tabListOverflowBoxStyles}>
          <ChakraTabList {...restProps} />
        </Box>
        {showLeftScrollButton && <TabListButtonLeft onClick={onClickButtonLeft} />}
        {showRightScrollButton && <TabListButtonRight onClick={onClickButtonRight} />}
      </Box>
    );
  }
);

TabList.displayName = DisplayNames.TabList;
TabList.propTypes = propTypes;
TabList.defaultProps = defaultProps;

export default TabList;
